import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Megaphone, Send, Clock, CheckCircle, AlertCircle, Image as ImageIcon, Trash2, Eye, Upload, Crop, Plus, ToggleLeft, ToggleRight, Loader2 } from 'lucide-react';
import notificationService from '../../services/notificationService';

const fmt = (dateStr) =>
  new Date(dateStr).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

export default function PromotionalNotifications() {
  const [activeTab, setActiveTab] = useState('broadcast'); // 'broadcast' | 'banners'
  const [isDragging, setIsDragging] = useState(false);
  
  // Broadcast state
  const [form, setForm] = useState({ title: '', body: '' });
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  // Banner state
  const [banners, setBanners] = useState([]);
  const [loadingBanners, setLoadingBanners] = useState(true);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [bannerTitle, setBannerTitle] = useState('');
  const [bannerPreview, setBannerPreview] = useState(null);
  const fileInputRef = useRef(null);

  const loadBanners = useCallback(async () => {
    setLoadingBanners(true);
    try {
      const data = await notificationService.getAllPromoBanners();
      setBanners(data || []);
    } catch (err) {
      console.error('Failed to load promo banners:', err);
    } finally {
      setLoadingBanners(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const data = await notificationService.getPromotionalHistory();
      setHistory(data || []);
    } catch {
      // silently fail
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    loadBanners();
    loadHistory();
  }, [loadBanners, loadHistory]);

  const processImageFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 700;
        canvas.height = 315;
        const ctx = canvas.getContext('2d');

        const targetRatio = 700 / 315;
        const imgRatio = img.width / img.height;
        let sourceX = 0, sourceY = 0, sourceWidth = img.width, sourceHeight = img.height;

        if (imgRatio > targetRatio) {
          sourceWidth = img.height * targetRatio;
          sourceX = (img.width - sourceWidth) / 2;
        } else {
          sourceHeight = img.width / targetRatio;
          sourceY = (img.height - sourceHeight) / 2;
        }

        ctx.drawImage(img, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, 700, 315);
        setBannerPreview(canvas.toDataURL('image/jpeg', 0.75));
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    processImageFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    processImageFile(file);
  };

  const handleUploadBanner = async (e) => {
    e.preventDefault();
    if (!bannerPreview) return;
    setUploadingBanner(true);
    try {
      await notificationService.createPromoBanner({
        imageUrl: bannerPreview,
        title: bannerTitle.trim() || undefined,
      });
      setBannerPreview(null);
      setBannerTitle('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      await loadBanners();
    } catch (err) {
      alert('Failed to upload banner');
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleToggleBannerStatus = async (banner) => {
    try {
      await notificationService.updatePromoBanner(banner.id, { isActive: !banner.isActive });
      loadBanners();
    } catch (err) {
      alert('Failed to update status');
    }
  };

  const handleDeleteBanner = async (id) => {
    if (!window.confirm('Delete this promo banner?')) return;
    try {
      await notificationService.deletePromoBanner(id);
      loadBanners();
    } catch (err) {
      alert('Failed to delete banner');
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) {
      setMessage({ type: 'error', text: 'Title and message are required.' });
      return;
    }
    setSending(true);
    setMessage(null);
    try {
      await notificationService.sendPromotional({ title: form.title.trim(), body: form.body.trim() });
      setMessage({ type: 'success', text: 'Promotion sent to all subscribed users!' });
      setForm({ title: '', body: '' });
      loadHistory();
    } catch {
      setMessage({ type: 'error', text: 'Failed to send promotion. Please try again.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#548B2E] rounded-lg flex items-center justify-center">
            <Megaphone className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Promotions &amp; Notifications</h1>
            <p className="text-sm text-gray-500">Send push broadcasts and manage shop screen hero banners</p>
          </div>
        </div>

        {/* Tab switcher - Push Broadcast FIRST */}
        <div className="flex bg-gray-100 p-1 rounded-xl w-fit">
          <button
            onClick={() => setActiveTab('broadcast')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
              activeTab === 'broadcast'
                ? 'bg-white text-[#548B2E] shadow-sm font-semibold'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Send className="w-4 h-4" />
            Push Broadcast
          </button>
          <button
            onClick={() => setActiveTab('banners')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
              activeTab === 'banners'
                ? 'bg-white text-[#548B2E] shadow-sm font-semibold'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <ImageIcon className="w-4 h-4" />
            Hero Banners ({banners.length})
          </button>
        </div>
      </div>

      {activeTab === 'banners' ? (
        <div className="space-y-6">
          {/* Easy-to-understand Upload card with Drag & Drop */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                  <Upload className="w-4 h-4 text-[#548B2E]" />
                  Add New Shop Hero Banner
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  Upload any image. It will automatically crop to fit the mobile app shop banner card.
                </p>
              </div>
              <span className="text-xs bg-green-50 text-green-700 font-semibold px-3 py-1 rounded-full border border-green-200">
                Step 1: Choose File → Step 2: Click Add
              </span>
            </div>

            <form onSubmit={handleUploadBanner} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                
                {/* Upload & Drag Drop Zone */}
                <div className="flex flex-col space-y-3">
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                      isDragging
                        ? 'border-[#548B2E] bg-green-50/50 scale-[0.99]'
                        : 'border-gray-300 hover:border-[#548B2E] hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <div className="w-12 h-12 bg-green-100 text-[#548B2E] rounded-full flex items-center justify-center mx-auto mb-3">
                      <Upload className="w-6 h-6" />
                    </div>
                    <p className="text-sm font-semibold text-gray-700">
                      Click to choose image or drag &amp; drop here
                    </p>
                    <p className="text-xs text-gray-400 mt-1">Supports JPG, PNG, WEBP</p>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Banner Tagline / Title (Optional)
                    </label>
                    <input
                      type="text"
                      value={bannerTitle}
                      onChange={(e) => setBannerTitle(e.target.value)}
                      placeholder="e.g. Fresh Vegetables 20% OFF"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
                    />
                  </div>
                </div>

                {/* Mobile Preview Box */}
                <div className="flex flex-col">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                    <Eye className="w-3.5 h-3.5 text-[#548B2E]" />
                    Mobile App Preview (How it will look)
                  </span>
                  <div className="flex-1 bg-gray-50 border border-gray-200 rounded-xl overflow-hidden min-h-[160px] flex items-center justify-center relative shadow-inner">
                    {bannerPreview ? (
                      <div className="w-full h-full relative">
                        <img
                          src={bannerPreview}
                          alt="Banner preview"
                          className="w-full h-full object-cover"
                        />
                        {bannerTitle && (
                          <div className="absolute left-3 bottom-3 bg-black/60 text-white text-xs px-2.5 py-1 rounded-lg font-semibold">
                            {bannerTitle}
                          </div>
                        )}
                        <span className="absolute top-2 right-2 text-[10px] bg-green-600 text-white font-bold px-2 py-0.5 rounded-full shadow">
                          Ready to Save
                        </span>
                      </div>
                    ) : (
                      <div className="text-center p-6 text-gray-400">
                        <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-30" />
                        <p className="text-xs font-medium text-gray-500">Image Preview Will Appear Here</p>
                        <p className="text-[11px] text-gray-400 mt-1">Select or drop an image on the left</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <button
                  type="submit"
                  disabled={!bannerPreview || uploadingBanner}
                  className="flex items-center gap-2 bg-[#548B2E] hover:bg-[#3f6b20] disabled:opacity-50 text-white px-6 py-2.5 rounded-lg font-semibold text-sm transition-colors shadow-sm"
                >
                  {uploadingBanner ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  {uploadingBanner ? 'Saving...' : 'Add Banner to App Carousel'}
                </button>
              </div>
            </form>
          </div>

          {/* Banner list card */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <h2 className="text-base font-semibold text-gray-800 mb-4 flex items-center justify-between">
              <span>Active &amp; Uploaded Banners</span>
              <span className="text-xs font-normal text-gray-500">
                If no banners are active, mobile app shows default static hero banner.
              </span>
            </h2>

            {loadingBanners ? (
              <div className="space-y-3">
                {[1, 2].map((i) => (
                  <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : banners.length === 0 ? (
              <div className="text-center py-12 text-gray-400 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                <ImageIcon className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm font-medium text-gray-600">No custom banners uploaded</p>
                <p className="text-xs text-gray-400 mt-1">The mobile app will automatically display the default static banner.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {banners.map((b) => (
                  <div
                    key={b.id}
                    className={`border rounded-xl p-3 flex gap-3 transition-all ${
                      b.isActive ? 'border-green-200 bg-green-50/20' : 'border-gray-200 opacity-60 bg-gray-50'
                    }`}
                  >
                    <div className="w-32 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 border border-gray-200">
                      <img src={b.imageUrl} alt={b.title || 'Banner'} className="w-full h-full object-cover" />
                    </div>

                    <div className="flex-1 flex flex-col justify-between">
                      <div>
                        <h3 className="text-sm font-medium text-gray-800 line-clamp-1">
                          {b.title || 'Untitled Banner'}
                        </h3>
                        <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full mt-1 ${
                          b.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                        }`}>
                          {b.isActive ? 'Active on App' : 'Disabled'}
                        </span>
                      </div>

                      <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
                        <button
                          onClick={() => handleToggleBannerStatus(b)}
                          className="text-xs flex items-center gap-1 text-gray-600 hover:text-gray-900 font-medium px-2 py-1 rounded hover:bg-gray-100"
                        >
                          {b.isActive ? (
                            <>
                              <ToggleRight className="w-4 h-4 text-green-600" /> Disable
                            </>
                          ) : (
                            <>
                              <ToggleLeft className="w-4 h-4 text-gray-400" /> Enable
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => handleDeleteBanner(b.id)}
                          className="text-xs flex items-center gap-1 text-red-600 hover:text-red-700 font-medium px-2 py-1 rounded hover:bg-red-50"
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Compose card */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-base font-semibold text-gray-700 mb-4">Compose Message</h2>

          {message && (
            <div className={`flex items-center gap-2 mb-4 px-4 py-3 rounded-lg text-sm ${
              message.type === 'success'
                ? 'bg-green-50 text-green-700'
                : 'bg-red-50 text-red-700'
            }`}>
              {message.type === 'success'
                ? <CheckCircle className="w-4 h-4 flex-shrink-0" />
                : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
              {message.text}
            </div>
          )}

          <form onSubmit={handleSend} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="e.g. 50% off on Fresh Vegetables Today!"
                maxLength={255}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                value={form.body}
                onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                placeholder="e.g. Use code FRESH50 at checkout. Valid today only!"
                rows={4}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E] focus:border-transparent resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={sending}
              className="w-full flex items-center justify-center gap-2 bg-[#548B2E] hover:bg-[#3f6b20] disabled:opacity-60 text-white px-4 py-2.5 rounded-lg font-medium text-sm transition-colors"
            >
              <Send className="w-4 h-4" />
              {sending ? 'Sending...' : 'Send to All Users'}
            </button>
          </form>
        </div>

        {/* History card */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-base font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Clock className="w-4 h-4 text-gray-400" />
            Sent History
          </h2>

          {loadingHistory ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-14 bg-gray-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <Megaphone className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No promotions sent yet</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
              {history.map((item) => (
                <div key={item.id} className="border border-gray-100 rounded-lg p-3">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium text-gray-800 leading-snug">{item.title}</p>
                    <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">{fmt(item.sentAt)}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">{item.body}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
